"use client";

import { Loader2 } from "lucide-react";
import { useLocale } from "next-intl";
import { useMemo, useState } from "react";
import { useArticleCategories, useArticles } from "@/hooks/useArticles";
import type { Locale } from "@/types/insights";
import { toArticle } from "./adapt";
import ArticleList from "./ArticleList";
import CategoryRail from "./CategoryRail";
import FeaturedArticle from "./FeaturedArticle";
import InsightsHeader from "./InsightsHeader";
import NewsletterBand from "./NewsletterBand";

/** Composes the Insights page from published backend articles + panel categories. */
export default function InsightsParent() {
  const ar = (useLocale() as Locale) === "ar";
  const [cat, setCat] = useState("all");
  const [count, setCount] = useState(6);
  const { data, isLoading, isError } = useArticles();
  const { data: cats = [] } = useArticleCategories();

  const railCats = useMemo(
    () => [{ id: "all", label: { ar: "الكل", en: "All" } }, ...cats],
    [cats],
  );

  const source = useMemo(
    () => (cat === "all" ? (data ?? []) : (data ?? []).filter((a) => a.category === cat)),
    [data, cat],
  );
  const articles = useMemo(() => source.map(toArticle), [source]);
  const featured = cat === "all" ? articles[0] : undefined;
  const list = cat === "all" ? articles.slice(1) : articles;

  return (
    <div className="bg-white text-ash dark:bg-ash dark:text-white">
      <InsightsHeader />
      <CategoryRail categories={railCats} active={cat} onChange={(k) => { setCat(k); setCount(6); }} />

      <div className="mx-auto max-w-360 space-y-16 px-6 py-16 lg:px-10">
        {isLoading ? (
          <div className="py-24 text-center"><Loader2 className="mx-auto h-7 w-7 animate-spin text-accent" /></div>
        ) : isError ? (
          <p className="py-24 text-center text-sm text-ash/50 dark:text-white/50">{ar ? "تعذّر تحميل المقالات" : "Couldn't load articles"}</p>
        ) : articles.length === 0 ? (
          <p className="py-24 text-center text-sm text-ash/50 dark:text-white/50">{ar ? "لا توجد مقالات" : "No articles"}</p>
        ) : (
          <>
            {featured && <FeaturedArticle article={featured} />}
            <ArticleList articles={list.slice(0, count)} />
            {count < list.length && (
              <div className="text-center">
                <button
                  onClick={() => setCount((c) => c + 3)}
                  className="border border-black/15 px-8 py-4 text-[12px] font-semibold uppercase tracking-widest text-ash transition-colors hover:border-accent hover:text-accent dark:border-white/15 dark:text-white"
                >
                  {ar ? "تحميل المزيد" : "Load more"}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <NewsletterBand />
    </div>
  );
}
